import React, { useEffect, useMemo } from 'react';
import { 
  CheckCircle, 
  Package, 
  DollarSign, 
  FileText, 
  ArrowRight,
  Building2,
  CalendarDays,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePaymentConditions, calculateDueDates } from '@/hooks/usePaymentConditions';
import { format } from 'date-fns';
import { PURCHASE_NATURE_LABELS } from '@/types/purchaseInvoice';
import { Label } from '@/components/ui/label';
import { PurchaseExpenseCategoryCombobox } from '@/components/Purchases/PurchaseExpenseCategoryCombobox';
import { usePurchaseCategorySelection } from '@/hooks/usePurchaseExpenseCategories';
import { useCostCenters } from '@/hooks/useCostCenters';
import { Combobox } from '@/components/ui/combobox';
import { effectiveItemType, type WizardData } from './index';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';
import { paymentCodeFromNfeTags } from '@/utils/nfePaymentMethod';

interface Step5ConfirmProps {
  data: WizardData;
  onUpdate: (updates: Partial<WizardData>) => void;
}

export function Step5Confirm({ data, onUpdate }: Step5ConfirmProps) {
  const { conditions: paymentConditions } = usePaymentConditions();
  // A-tric.10: TODOS os itens da nota entram na compra (sem produto = product_id NULL)
  const mappedProducts = data.productMappings;

  // C-6 (D-C.5/D-C.8): categorias do cabeçalho — mesma lista e ranking do F0. O campo de uso
  // interno só existe quando algum item tem tipo EFETIVO 'internal_use'. O estado nasce do
  // wizardData (voltar e avançar não perde a escolha) e é espelhado nele para o handleFinish.
  const hasInternalUse = mappedProducts.some(
    (m) => effectiveItemType(m, data.nature) === 'internal_use',
  );
  const {
    options: expenseCategoryOptions,
    isLoading: loadingCategories,
    expenseCategoryId,
    internalUseCategoryId,
    selectExpenseCategory,
    selectInternalUseCategory,
  } = usePurchaseCategorySelection({
    hasInternalUse,
    initialExpenseCategoryId: data.expenseCategoryId,
    initialInternalUseCategoryId: data.internalUseCategoryId,
  });

  useEffect(() => {
    if (
      (data.expenseCategoryId ?? '') === expenseCategoryId &&
      (data.internalUseCategoryId ?? '') === internalUseCategoryId
    ) return;
    onUpdate({ expenseCategoryId, internalUseCategoryId });
    // onUpdate muda de identidade a cada render do wizard — deps só nos valores
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseCategoryId, internalUseCategoryId]);

  // FIN-FORMA-OBRIGATORIA Ato 1 (I.168, A4 do Diretor): forma pré-marcada uma vez pela tag de
  // pagamento da nota (forma igual no cadastro); senão vazia e obrigatória. Vive no wizardData.
  const { methods: paymentMethods } = usePaymentMethods();
  useEffect(() => {
    if (data.paymentMethod !== undefined || paymentMethods.length === 0) return;
    const detPag = data.parsedXml?.pag?.detPag;
    const tags = (Array.isArray(detPag) ? detPag : detPag ? [detPag] : [])
      .map((p: { tPag?: string }) => p?.tPag);
    onUpdate({ paymentMethod: paymentCodeFromNfeTags(tags, paymentMethods.map((m) => m.code)) });
    // onUpdate muda de identidade a cada render do wizard — deps só nos valores
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.paymentMethod, paymentMethods, data.parsedXml]);

  // R-COMPRAS.18: Centro de Custo do cabeçalho — opcional, sem pré-seleção. Sem state local:
  // o valor vive no wizardData (voltar e avançar preserva) e o handleFinish lê de lá.
  const { centers: costCenters } = useCostCenters();
  const costCenterOptions = costCenters.map((cc) => ({
    value: cc.id,
    label: cc.center_name,
  }));

  // A-tric.8 / N-FNF.1: estoque = item COM produto E movimentador
  // (motor: COALESCE(item_type,'resale') <> 'internal_use'); uso interno = rastro sem movimento
  const movimentaProducts = mappedProducts.filter(
    m => !!m.productId && effectiveItemType(m, data.nature) !== 'internal_use',
  );
  const movimentaCount = movimentaProducts.length;
  const totalValue = data.invoice.total_value || 0;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  // Determine installments to show
  const xmlDuplicatas = Array.isArray(data.parsedXml?.cobr?.dup) ? data.parsedXml.cobr.dup : [];
  const hasXmlDuplicatas = xmlDuplicatas.length > 0;

  const calculatedDueDates = useMemo(() => {
    if (hasXmlDuplicatas || !data.paymentConditionId) return [];
    const selectedCondition = paymentConditions.find(c => c.id === data.paymentConditionId);
    if (!selectedCondition) return [];
    const baseDate = data.invoice.emission_date ? new Date(data.invoice.emission_date) : new Date();
    return calculateDueDates(selectedCondition, baseDate);
  }, [data.paymentConditionId, paymentConditions, hasXmlDuplicatas, data.invoice.emission_date]);

  // Build unified installment list for display
  const installments = useMemo(() => {
    if (hasXmlDuplicatas) {
      const n = xmlDuplicatas.length;
      return xmlDuplicatas.map((dup: any, i: number) => {
        const parsedValue = parseFloat(dup.vDup);
        if (!isNaN(parsedValue) && parsedValue > 0) {
          return {
            number: dup.nDup || `${i + 1}/${n}`,
            dueDate: dup.dVenc || null,
            value: parsedValue,
          };
        }
        // Fallback penny-perfect quando vDup está vazio
        const baseValue = Math.floor((totalValue / n) * 100) / 100;
        const remainder = Math.round((totalValue - baseValue * n) * 100) / 100;
        return {
          number: dup.nDup || `${i + 1}/${n}`,
          dueDate: dup.dVenc || null,
          value: i === n - 1
            ? Math.round((baseValue + remainder) * 100) / 100
            : baseValue,
        };
      });
    }
    if (calculatedDueDates.length > 0) {
      const n = calculatedDueDates.length;
      const baseValue = Math.floor((totalValue / n) * 100) / 100;
      const remainder = Math.round((totalValue - baseValue * n) * 100) / 100;
      return calculatedDueDates.map((date, i) => ({
        number: `${i + 1}/${n}`,
        dueDate: format(date, 'yyyy-MM-dd'),
        value: i === n - 1
          ? Math.round((baseValue + remainder) * 100) / 100
          : baseValue,
      }));
    }
    // Single installment fallback
    return [{
      number: '1/1',
      dueDate: data.invoice.emission_date || format(new Date(), 'yyyy-MM-dd'),
      value: totalValue,
    }];
  }, [hasXmlDuplicatas, xmlDuplicatas, calculatedDueDates, totalValue, data.invoice.emission_date]);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold">Pronto para Importar!</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Confira o resumo abaixo e clique em "Concluir Importação"
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Invoice Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Nota Fiscal
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Número</span>
              <span className="font-medium">
                {data.invoice.invoice_series || '1'}-{data.invoice.invoice_number}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Fornecedor</span>
              <span className="font-medium text-right max-w-[200px] truncate">
                {data.invoice.emitente_razao_social}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Data Emissão</span>
              <span className="font-medium">
                {data.invoice.emission_date 
                  ? new Date(data.invoice.emission_date).toLocaleDateString('pt-BR')
                  : '-'}
              </span>
            </div>
            <Separator />
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Valor Total</span>
              <span className="text-lg font-bold text-primary">
                {formatCurrency(totalValue)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Classification */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Classificação
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Natureza</span>
              <Badge variant="outline">
                {PURCHASE_NATURE_LABELS[data.nature]}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Movimenta Estoque</span>
              <Badge
                variant="outline"
                className={movimentaCount > 0
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : 'bg-gray-50'}
              >
                {movimentaCount > 0 ? `Sim (${movimentaCount} ${movimentaCount > 1 ? 'itens' : 'item'})` : 'Não'}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Gerar Contas a Pagar</span>
              <Badge 
                variant="outline" 
                className={data.generateFinancial 
                  ? 'bg-blue-50 text-blue-700 border-blue-200' 
                  : 'bg-gray-50'}
              >
                {data.generateFinancial ? `Sim (${installments.length}x)` : 'Não'}
              </Badge>
            </div>

            {/* C-6 (D-C.5): categorias do cabeçalho da compra */}
            <Separator />
            <div className="space-y-2">
              <Label>Categoria de Despesa</Label>
              <PurchaseExpenseCategoryCombobox
                value={expenseCategoryId}
                onChange={selectExpenseCategory}
                options={expenseCategoryOptions}
                isLoading={loadingCategories}
              />
            </div>
            {hasInternalUse && (
              <div className="space-y-2">
                <Label>Categoria de uso interno</Label>
                <PurchaseExpenseCategoryCombobox
                  value={internalUseCategoryId}
                  onChange={selectInternalUseCategory}
                  options={expenseCategoryOptions}
                  isLoading={loadingCategories}
                  placeholder="Selecione a categoria dos itens de uso interno..."
                />
              </div>
            )}

            {/* FIN-FORMA-OBRIGATORIA Ato 1 (I.168): obrigatória; pré-marcada pela tag de pagamento da nota */}
            <div className="space-y-2">
              <Label>Forma de Pagamento *</Label>
              <Select
                value={data.paymentMethod ?? ''}
                onValueChange={(value) => onUpdate({ paymentMethod: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a forma de pagamento" />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.filter((method) => !!method.code).map((method) => (
                    <SelectItem key={method.id} value={method.code as string}>
                      {method.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!data.paymentMethod && (
                <p className="text-xs text-muted-foreground">
                  A nota não define uma forma do cadastro — escolha a forma de pagamento.
                </p>
              )}
            </div>

            {/* R-COMPRAS.18: Centro de Custo do cabeçalho (molde C-6 NewPayableDialog) */}
            <div className="space-y-2">
              <Label>Centro de Custo</Label>
              <Combobox
                options={costCenterOptions}
                value={data.costCenterId ?? ''}
                onValueChange={(value) => onUpdate({ costCenterId: value })}
                placeholder="Selecione o centro de custo"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Products Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Itens da nota ({mappedProducts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-h-48 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="text-left p-2">Produto</th>
                  <th className="text-left p-2">Tipo</th>
                  <th className="text-right p-2">Qtd (nota)</th>
                  <th className="text-left p-2">Unidade</th>
                  <th className="text-right p-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {mappedProducts.map((mapping, index) => (
                  <tr key={index} className="border-b">
                    <td className="p-2">
                      <div className="font-medium">{mapping.productName}</div>
                    </td>
                    <td className="p-2">
                      {(() => {
                        const t = effectiveItemType(mapping, data.nature);
                        return (
                          <Badge variant={t === 'internal_use' ? 'secondary' : 'outline'} className="text-[10px]">
                            {t === 'internal_use' ? 'Uso Interno' : 'Revenda'}
                          </Badge>
                        );
                      })()}
                    </td>
                    <td className="text-right p-2 text-muted-foreground">
                      {mapping.quantity} {mapping.xmlItem?.uCom}
                    </td>
                    {/* I.151 1b3-b: unidade escolhida do produto; a conversão é do banco */}
                    <td className="p-2">
                      {mapping.productId ? (mapping.unitLabel || '-') : '-'}
                    </td>
                    <td className="text-right p-2 font-medium">
                      {formatCurrency(mapping.quantity * mapping.unitPrice)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 pt-4 border-t flex justify-between items-center">
            <span className="text-muted-foreground">
              Itens que dão entrada no estoque (convertidos pelo sistema para a unidade base)
            </span>
            <span className="text-lg font-bold">
              {movimentaCount}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Installments Summary */}
      {data.generateFinancial && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              Parcelas no Contas a Pagar ({installments.length})
              {hasXmlDuplicatas && (
                <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100 ml-2 text-xs">
                  XML
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Parcela</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {installments.map((inst, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{inst.number}</TableCell>
                    <TableCell>
                      {inst.dueDate 
                        ? format(new Date(inst.dueDate + 'T12:00:00'), 'dd/MM/yyyy')
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(inst.value)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Actions Summary */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <h4 className="font-medium mb-3">Ao confirmar, as seguintes ações serão executadas:</h4>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Criar registro de compra com {mappedProducts.length} item(ns)
            </li>
            {movimentaCount > 0 && (
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                Dar entrada no estoque de {movimentaCount} {movimentaCount > 1 ? 'itens' : 'item'}, na unidade base de cada produto
              </li>
            )}
            {data.generateFinancial && (
              <li className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-600" />
                Criar {installments.length} parcela{installments.length > 1 ? 's' : ''} no Contas a Pagar totalizando{' '}
                {formatCurrency(totalValue)}
              </li>
            )}
            <li className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Registrar manifestação "Confirmação da Operação" na SEFAZ
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Memorizar produto e unidade deste fornecedor para as próximas notas
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
